import createFetchSelectors from './createFetchSelectors';
import createPerSliceIdSelector from '../helpers/createPerSliceIdSelector';
import { getSliceClickthrough } from './content';
import makeItemLink from '../helpers/makeItemLink';
import { getSliceItemIdsWithVisibility, sliceHasCategories } from './categorised';
import get from '../helpers/get';
import isCategoryFilter from '../helpers/isCategoryFilter';
import filterUnique from '../helpers/filterUnique';

const {
  getPageSize: getProductsPageSize,
  getTotalCount: getTotalProductsCount,
  getPage: getProductsPage,
  getMultipleRequests: getProductMultipleRequests,
  hasMorePages: hasMoreProductsPages,
  getFilters: getProductsFilters,
  getSliceIds: getProductsSliceIds,
  getItems,
  isBusy: isProductsBusy,
  getError: getProductsError,
  isReadyOrError: isProductsReadyOrError,
} = createFetchSelectors({
  stateSliceName: 'products',
  itemTypePlural: 'products',
  defaultPageSize: 6,
});

export {
  getProductsPageSize,
  getProductMultipleRequests,
  getTotalProductsCount,
  getProductsPage,
  hasMoreProductsPages,
  getProductsFilters,
  getProductsSliceIds,
  isProductsBusy,
  getProductsError,
  isProductsReadyOrError,
};

export const isProductsSliceSponsored = (state, sliceId) => (
  state.products?.slices?.[sliceId]?.isSponsored || false
);

const transformProduct = (product, clickthrough) => ({
  ...product,
  format: 'portrait',
  link: product.sku ? makeItemLink(product, clickthrough, 'sku') : undefined,
});

export const getProducts = createPerSliceIdSelector(
  getItems,
  getSliceClickthrough,
  (items, clickthrough) => items.map(product => transformProduct(product, clickthrough)),
);

const assignFilterValues = (obj, key, value) => {
  const values = obj[key] || [];
  switch (key) {
    case 'new':
      values.push(parseFloat(value));
      break;
    case 'gender':
      values.splice(0, 0, ...[value, 'unisex']);
      break;
    default:
      values.push(value);
  }
  return {
    ...obj,
    [key]: filterUnique(values),
  };
};

export const collectFilters = (map, { id, visible }) => {
  if (visible) {
    return assignFilterValues(map, ...id.split('='));
  }
  return map;
};

export const mapCategoriesFilterAndId = itemIdsWithVisibility => {
  const mappedCategories = (itemIdsWithVisibility || [])
    .reduce(({ ids, mappedFilters }, item) => {
      if (isCategoryFilter(item.id)) {
        return {
          ids,
          mappedFilters: collectFilters(mappedFilters, item),
        };
      }
      return {
        mappedFilters,
        ids: [...ids, item],
      };
    }, {
      mappedFilters: {},
      ids: [],
    });
  return {
    ...mappedCategories,
    mappedFilters: Object.entries(mappedCategories.mappedFilters),
  };
};

export const getProductsByFilter = (productsMap, mappedFilters, clickthrough = {}) => {
  let filteredProducts = Object.values(productsMap)
    .filter(({ filters }) => mappedFilters
      .reduce((isValid, [key, value]) => {
        if (!filters || !isValid) {
          return false;
        }
        if (Array.isArray(filters[key])) {
          return value.some(item => filters[key].includes(item));
        }
        return value.includes(filters[key]);
      }, true));

  filteredProducts = filteredProducts.length ? filteredProducts : [{}];

  return filteredProducts.map(product => ({
    ...transformProduct(product, clickthrough),
    visible: true,
  }));
};

export const getProductsWithVisibility = createPerSliceIdSelector(
  state => get(state, ['products', 'products']) || {},
  getSliceItemIdsWithVisibility,
  getSliceClickthrough,
  sliceHasCategories,
  getProducts,
  (productsMap, itemIdsWithVisibility, clickthrough, hasCategories, products) => {
    if (!hasCategories) {
      return products && products.map(x => ({ ...x, visible: true }));
    }
    const { ids, mappedFilters } = mapCategoriesFilterAndId(itemIdsWithVisibility);

    if (mappedFilters.length) {
      return getProductsByFilter(productsMap, mappedFilters, clickthrough);
    }

    return ids.map(({ id, visible }) => ({
      ...transformProduct(productsMap[id] || {}, clickthrough),
      visible: visible || !hasCategories,
    }));
  },
);
